import { Component, createRef, KeyboardEvent } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { AppState } from 'src/AppState';
import {
	CaseGetControlQueryResultValidationResult,
	DocumentFeature,
	EasyClaimAppState,
	EasyClaimCaseResponseDto,
	EasyClaimDocumentType,
	ValidationResult,
	VehicleDto,
	VehicleType,
} from 'src/easyclaim.api.client.typescript/src';
import { MemoPlaceholderScooterOdometer } from 'src/easyclaim.app.assets/artwork/placeholder/v2/PlaceholderScooterOdometer';
import {
	DetailsContainer,
	ExampleModalImage,
	Input,
	InputFloatingPlaceholder,
	SelectImage,
	StyledInfoIcon,
} from 'src/easyclaim.app.components';
import { CompanyCheck } from 'src/easyclaim.app.components/common/CompanyCheck';
import {
	FeedbackMode,
	ImageRecognitionFeedback2,
} from 'src/easyclaim.app.components/common/ImageRecognitionFeedback2';
import { OdometerPlaceholder } from 'src/easyclaim.app.components/common/ImageRepository';
import ShowExamplePhoto from 'src/easyclaim.app.components/common/ShowPhotoExample';
import { reportError } from 'src/easyclaim.app.libs/reportError';
import { ImageSelectionService, SelectState } from 'src/easyclaim.app.services';
import { Image } from 'src/easyclaim.app.services/ImageSelectionService';

import { OdometerImageV2 } from '../SInglePerspectives/OdometerImageV2';

interface OdometerAndMileageControlState {
	mileage: number | null;
	dashboardMileageTextValidationResults: CaseGetControlQueryResultValidationResult[] | null;
	isDashboardMileageTextInputValidationVisible: boolean;
}

export type OdometerControlInputType = 'Default' | 'FloatingPlaceholder';

interface OdometerAndMileageControlProps {
	appState: AppState;
	screen: EasyClaimAppState;
	odometerTextInputType: OdometerControlInputType;
	enableMotorbikesSupport: boolean;
	intl: IntlShape;
}

//dashboardImage
function dashboardImageChanged(currentAppState: AppState, image: Image | undefined): AppState {
	let images: Image[] = [];

	images = currentAppState.overallDashboardImage
		? images.concat([currentAppState.overallDashboardImage])
		: images;
	images = image ? images.concat([image]) : images;
	images = currentAppState.windshieldImage
		? images.concat([currentAppState.windshieldImage])
		: images;
	images = currentAppState.vinImage ? images.concat([currentAppState.vinImage]) : images;

	const isImageLoading = isAnyOfImagesLoading(images);

	const newState = Object.assign({}, currentAppState, {
		dashboardImage: image,
		isImageLoading: isImageLoading,
		imputError: false,
		isOdometerAndMileageScreenCompleated: false,
	});

	return newState;
}

// Retake/manual-input warnings describe the photo and are shown in its own feedback; correct results still carry the recognized mileage
function getMileageTextValidationResults(
	validationResults: CaseGetControlQueryResultValidationResult[] | null | undefined
): CaseGetControlQueryResultValidationResult[] {
	return (validationResults ?? []).filter(
		(vr) =>
			vr.validationResult === ValidationResult.Correct ||
			(vr.feature !== DocumentFeature.ExpectedMileageRetake &&
				vr.feature !== DocumentFeature.ExpectedMileageManualInput)
	);
}

function isAnyOfImagesLoading(images: Image[]): boolean {
	const result = !!images && images.length > 0 && images.some(ImageSelectionService.isImageLoading);

	return result;
}

export class OdometerAndMileageControl extends Component<
	OdometerAndMileageControlProps,
	OdometerAndMileageControlState
> {
	private unmounted: boolean = false;
	private selectImageRef = createRef<any>();
	private isMileageUploadLimitEnabled: boolean =
		this.props.appState.appService.isMileageUploadLimitEnabled();

	constructor(props: OdometerAndMileageControlProps) {
		super(props);

		const isDashboardMileageTextInputValidationVisible =
			props.appState.appService.isDashboardMileageTextInputValidationVisible(this.screen);
		const dashboardMileageTextValidationResults =
			props.appState.appService.getDashboardMileageTextValidationResults(this.screen);

		this.state = {
			mileage: this.easyClaimCase?.vehicle?.userProvidedMileage ?? null,
			isDashboardMileageTextInputValidationVisible: isDashboardMileageTextInputValidationVisible,
			dashboardMileageTextValidationResults: dashboardMileageTextValidationResults,
		};

		this.onMileageTextChanged = this.onMileageTextChanged.bind(this);
	}

	static getDerivedStateFromProps(
		props: OdometerAndMileageControlProps,
		state: OdometerAndMileageControlState
	): any {
		if (
			state.mileage !== props.appState.easyClaimCase?.vehicle?.userProvidedMileage &&
			props.appState.easyClaimCase?.vehicle?.userProvidedMileage !== undefined
		) {
			return { mileage: props.appState.easyClaimCase?.vehicle?.userProvidedMileage };
		}

		return state;
	}

	get screen() {
		return this.props.screen;
	}

	get easyClaimCase() {
		return this.props.appState.easyClaimCase;
	}

	get easyClaimService() {
		return this.props.appState.easyClaimService;
	}

	get appService() {
		return this.props.appState.appService;
	}

	get dashboardImage() {
		return this.props.appState.dashboardImage;
	}

	componentDidMount(): void {
		void this.loadScreenControl().catch((error: unknown) => {
			reportError(error, {
				context: 'OdometerAndMileageControl.componentDidMount.loadScreenControl',
			});
		});
	}

	private async loadScreenControl(): Promise<void> {
		if (!this.easyClaimCase?.caseId) {
			return;
		}

		if (this.unmounted === false) {
			const result = await this.easyClaimService?.getScreenControl(
				this.easyClaimCase?.caseId,
				this.screen,
				'dashboardMileageTextInput'
			);

			if (this.unmounted === false) {
				if (this.props.appState.isMileageDirty) {
					this.setState({
						isDashboardMileageTextInputValidationVisible: !!result?.validationResults?.length,
						dashboardMileageTextValidationResults: result?.validationResults || [],
					});

					return;
				}

				const mileage = result?.value ? parseInt(result?.value, 10) : null;

				this.setState({
					isDashboardMileageTextInputValidationVisible: !!result?.validationResults?.length,
					dashboardMileageTextValidationResults: result?.validationResults || [],
					mileage: mileage,
				});

				const isMileageValid = this.appService.isMileageValid(
					this.props.appState,
					this.screen,
					mileage
				);
				const mileageMatchesAppState = this.easyClaimCase?.vehicle?.userProvidedMileage === mileage;

				if (
					isMileageValid !== this.props.appState.isDashboardMileageTextInputCompleted ||
					!mileageMatchesAppState
				) {
					const newAppState: AppState = {
						...this.props.appState,
						isDashboardMileageTextInputCompleted: isMileageValid,
						isMileageDirty: false,
						easyClaimCase: {
							...this.easyClaimCase,
							vehicle: { ...this.easyClaimCase?.vehicle, userProvidedMileage: mileage },
						},
					};

					await this.props.appState.setAppState(newAppState);
				} else {
					await this.props.appState.setAppState({
						...this.props.appState,
						isMileageDirty: false,
					});
				}
			}
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	onMileageKeyDown(ev: KeyboardEvent<HTMLInputElement>) {
		const isDigit = /^[0-9]$/.test(ev.key);

		if (!isDigit) ev.preventDefault();
	}

	onMileageTextChanged(ev: any, ...args: any[]) {
		const text = ev.target.value.replace(/[^0-9]/g, '');

		const mileage: number | null = this.appService.parseProvidedMileageStringToNumber(
			text,
			this.state.mileage
		);

		this.setState({
			mileage: mileage,
		});

		const currentVehicle = this.easyClaimCase?.vehicle;

		const newVehicle: VehicleDto = Object.assign({}, currentVehicle, {
			platNumber: this.easyClaimCase?.vehicle?.platNumber,
			userProvidedMileage: mileage,
		});

		const newEasyClaimCase: EasyClaimCaseResponseDto = Object.assign({}, this.easyClaimCase, {
			vehicle: newVehicle,
		});

		const newAppState: AppState = Object.assign({}, this.props.appState, {
			easyClaimCase: newEasyClaimCase,
			isDashboardMileageTextInputCompleted: this.appService.isMileageValid(
				this.props.appState,
				this.screen,
				mileage
			),
			isMileageDirty: true,
			inputError: false,
		});

		void this.props.appState.setAppState(newAppState);
	}

	showExampleModal(motorbikesSupport: boolean, vehicleTypeId: VehicleType | undefined) {
		const isDashboardMileageScreen = this.screen === EasyClaimAppState.DashboardMileage;

		const headerTitleId: string = isDashboardMileageScreen
			? 'dashboardImageExampleModal.headline'
			: 'fourAdditionalPictures.dashboard.exampleModalTitle';

		const shouldUseWebp =
			this.props.appState.easyClaimCase?._configuration?.canUseExperimentalPerformanceEnhancer ===
			true;

		const images: ExampleModalImage[] =
			motorbikesSupport && vehicleTypeId === VehicleType.Motorbike
				? //@ts-ignore
					[{ src: `${process.env.PUBLIC_URL}/images/example-policycheck-motorbike-odometer.png` }]
				: shouldUseWebp
					? //@ts-ignore
						[{ src: `${process.env.PUBLIC_URL}/images/WEBP/example-dashboard-1.webp` }]
					: //@ts-ignore
						[{ src: `${process.env.PUBLIC_URL}/images/example-dashboard-1.jpg` }];

		return this.props.appState.showExampleModal(
			images,
			headerTitleId,
			'fourAdditionalPicturesExampleModal.button.close',
			'mileage'
		);
	}

	getOdometerInput() {
		const isDashboardMileageTextInputRequired = this.appService.isDashboardMileageTextInputRequired(
			this.screen
		);
		const isMileageValid = this.appService.isMileageValid(
			this.props.appState,
			this.screen,
			this.state.mileage
		);
		const inputError = this.isMileageUploadLimitEnabled
			? !isMileageValid
			: this.props.appState.inputError;
		const showInputPlaceholder = this.isMileageUploadLimitEnabled
			? inputError
				? true
				: false
			: true;
		const showUpdatedVerificationResults =
			this.props.appState.easyClaimCase?._configuration?.showUpdatedVerificationResults;
		const placeholderId = showUpdatedVerificationResults
			? 'dashboardMileage.placeholder.updated'
			: 'dashboardMileage.placeholder';

		if (this.props.odometerTextInputType === 'FloatingPlaceholder') {
			return (
				<InputFloatingPlaceholder
					className={inputError ? 'mileage-input-floating input-error' : 'mileage-input-floating'}
					type="text"
					inputMode="numeric"
					pattern="[0-9]+"
					placeholder={
						showInputPlaceholder ? this.props.intl.formatMessage({ id: placeholderId }) : undefined
					}
					isValid={true}
					showInvalid={true}
					hasIcon={true}
					inputError={inputError}
					isRequired={isDashboardMileageTextInputRequired}
					value={this.state.mileage ? this.state.mileage : ''}
					onChange={this.onMileageTextChanged}
				/>
			);
		} else {
			return (
				<Input
					className="mileage-input"
					type="text"
					inputMode="numeric"
					pattern="[0-9]+"
					placeholder={this.props.intl.formatMessage({ id: placeholderId })}
					isValid={isMileageValid}
					showInvalid={true}
					isRequired={isDashboardMileageTextInputRequired}
					value={this.state.mileage ? this.state.mileage : ''}
					onChange={this.onMileageTextChanged}
				/>
			);
		}
	}

	// With the upload limit, the input is shown once a photo is processed (correct or manual input), but not on retake
	private isMileageInputVisible(image: Image | undefined): boolean {
		if (!this.isMileageUploadLimitEnabled) {
			return true;
		}

		const validationResults = image?.uploadResult?.validationResults;

		return (
			!!validationResults?.length &&
			!validationResults.some((vr) => vr.feature === DocumentFeature.ExpectedMileageRetake)
		);
	}

	private isMileageTextValidationDisplayed(image: Image | undefined): boolean {
		const isSuppressed = !!image?.uploadResult?.validationResults?.some(
			(vr) =>
				vr.feature === DocumentFeature.ExpectedMileageRetake ||
				vr.feature === DocumentFeature.ExpectedMileageManualInput
		);

		return (
			this.appService.isDashboardMileageTextInputVisible(this.screen) &&
			this.isMileageInputVisible(image) &&
			!isSuppressed
		);
	}

	private imageChangeRequestId = 0;

	private onImageChange = async (image: Image | undefined) => {
		const requestId = ++this.imageChangeRequestId;
		const newAppState = dashboardImageChanged(this.props.appState, image);

		await this.props.appState.setAppState(newAppState);

		if (
			this.easyClaimCase?.caseId &&
			(image === undefined ||
				(image?.selectState &&
					[SelectState.Uploaded, SelectState.UnprocessableEntity].includes(image?.selectState)))
		) {
			await this.easyClaimService?.invalidateScreenControlCache(
				this.easyClaimCase.caseId,
				this.screen,
				'dashboardMileageTextInput'
			);
			const result = await this.easyClaimService?.getScreenControl(
				this.easyClaimCase?.caseId,
				this.screen,
				'dashboardMileageTextInput'
			);

			// A newer image change started while this response was pending; its result must win
			if (requestId !== this.imageChangeRequestId) {
				return;
			}

			const mileage = result?.value;
			// Some processes return a warning for autofilled mileage; auto move next only when it is not displayed
			const hasMileageTextWarningsOrErrors =
				this.isMileageTextValidationDisplayed(image) &&
				getMileageTextValidationResults(result?.validationResults).some(
					(vr) =>
						vr.validationResult === ValidationResult.Warning ||
						vr.validationResult === ValidationResult.Error
				);

			const newState = Object.assign({}, this.state, {
				isDashboardMileageTextInputValidationVisible: !!result?.validationResults?.length,
				dashboardMileageTextValidationResults: result?.validationResults,
			});

			this.setState(newState);

			const currentVehicle = this.easyClaimCase?.vehicle;

			const newVehicle: VehicleDto = Object.assign({}, currentVehicle, {
				userProvidedMileage: mileage,
			});

			const newEasyClaimCase: EasyClaimCaseResponseDto = Object.assign({}, this.easyClaimCase, {
				vehicle: newVehicle,
			});

			const appStateWithImage = { ...this.props.appState, dashboardImage: image };
			const newAppState: AppState = Object.assign({}, appStateWithImage, {
				easyClaimCase: newEasyClaimCase,
				inputError: false,
				isDashboardMileageTextInputCompleted: this.appService.isMileageValid(
					appStateWithImage,
					this.screen,
					mileage
				),
				isOdometerAndMileageScreenCompleated:
					image?.selectState === SelectState.Uploaded && !hasMileageTextWarningsOrErrors,
			});

			await this.props.appState.setAppState(newAppState);
		}
	};

	getPlaceholder(motorbikesSupport: boolean, vehicleTypeId: VehicleType | undefined): JSX.Element {
		return motorbikesSupport && vehicleTypeId === VehicleType.Motorbike ? (
			<MemoPlaceholderScooterOdometer className="photo-placeholder" />
		) : (
			<OdometerPlaceholder />
		);
	}

	render() {
		const { dashboardImage } = this.props.appState;
		const isDashboardImageVisible = this.appService.isDashboardImageVisible(this.screen);
		const isDashboardMileageTextInputVisible = this.appService.isDashboardMileageTextInputVisible(
			this.screen
		);
		const motorbikesSupport = this.props.enableMotorbikesSupport;
		const vehicleTypeId = this.props.appState.easyClaimCase?.vehicle?.vehicleTypeId;
		const placeholder = this.getPlaceholder(motorbikesSupport, vehicleTypeId);
		const isUniqa = CompanyCheck.isUniqaPL(
			this.props.appState.easyClaimCase?._configuration?.companyShortName
		);
		const useSolvdStyles = !!this.props.appState.easyClaimCase?._configuration?.useSolvdStyles;
		const groupFeedback = isUniqa;
		const dashboardImageValidationResults =
			this.props.appState.dashboardImage?.uploadResult?.validationResults;
		const isRetake = !!dashboardImageValidationResults?.some(
			(vr) => vr.feature === DocumentFeature.ExpectedMileageRetake
		);
		const isManualInput = !!dashboardImageValidationResults?.some(
			(vr) => vr.feature === DocumentFeature.ExpectedMileageManualInput
		);
		const isMileageDetailContainerVisible = isDashboardMileageTextInputVisible && !isRetake;
		const isMileageInputVisible = this.isMileageInputVisible(this.props.appState.dashboardImage);
		const mileageTextValidationResults = getMileageTextValidationResults(
			this.state.dashboardMileageTextValidationResults
		);
		const hasMileageTextValidationResults =
			this.state.isDashboardMileageTextInputValidationVisible &&
			mileageTextValidationResults.length > 0;
		const isDashboardMileageTextInputValidationVisible =
			hasMileageTextValidationResults && !isRetake && !isManualInput && isMileageInputVisible;

		return (
			<>
				<DetailsContainer className="mileage-container-original">
					{isDashboardImageVisible && useSolvdStyles && (
						<OdometerImageV2
							caseId={this.easyClaimCase!.caseId}
							selectImageRef={this.selectImageRef}
							easyClaimService={this.easyClaimService}
							currentImage={dashboardImage}
							inputError={this.props.appState.inputError}
							vehicleType={vehicleTypeId}
							onChange={(image) => {
								void this.onImageChange(image).catch((error: unknown) => {
									reportError(error, {
										context: 'OdometerAndMileageControl.OdometerImageV2.onChange',
									});
								});
							}}
							isDashboardMileageTextInputVisible={isMileageDetailContainerVisible}
							isDashboardMileageTextInputValidationVisible={
								hasMileageTextValidationResults && !isManualInput
							}
							componentId={this.dashboardImage?.componentId}
							dashboardMileageTextValidationResults={mileageTextValidationResults}
							groupFeedback={groupFeedback}
							odometerInput={
								isMileageInputVisible ? (
									<>
										<p className="four-additional-pictures-mileage-paragraph-first">
											<FormattedMessage id="dashboardMileage.paragraph.first" />
										</p>
										{this.getOdometerInput()}
									</>
								) : null
							}
						/>
					)}
					{isDashboardImageVisible && !useSolvdStyles && (
						<div className="odometer-and-mileage-detail-container">
							<h3 className="four-additional-pictures-mileage-title">
								<FormattedMessage id="fourAdditionalPictures.mileage.title" />
							</h3>
							<p className="four-additional-pictures-mileage-paragraph">
								<FormattedMessage id="dashboardMileage.dashboard.paragraph"></FormattedMessage>
								<StyledInfoIcon
									onClick={() => {
										this.showExampleModal(motorbikesSupport, vehicleTypeId).catch(reportError);
									}}
								/>
								<span>
									<ShowExamplePhoto
										id="four-diagonal-perspectives-vehicle-front-left-description"
										onClick={() => {
											this.showExampleModal(motorbikesSupport, vehicleTypeId).catch(reportError);
										}}
									>
										{(description) => (
											<span className="four-diagonal-perspectives-vehicle-front-left-description">
												{description}
											</span>
										)}
									</ShowExamplePhoto>
								</span>
							</p>
							<SelectImage
								caseId={this.easyClaimCase!.caseId}
								componentId="dashboardPicture"
								fileType={EasyClaimDocumentType.DashboardPicture}
								currentImage={dashboardImage}
								title="dashboardMileage.dashboard.title"
								onChange={(image) => {
									void this.onImageChange(image).catch((error: unknown) => {
										reportError(error, {
											context: 'OdometerAndMileageControl.SelectImage.onChage ',
										});
									});
								}}
								easyClaimService={this.easyClaimService}
								placeholder={placeholder}
								configuration={this.easyClaimCase!._configuration!}
								showUserFeedbackInfo={true}
								onShowExample={() => {
									this.showExampleModal(motorbikesSupport, vehicleTypeId).catch(reportError);
								}}
								inputError={this.props.appState.inputError}
								forwardedRef={this.selectImageRef}
							/>
						</div>
					)}
					{isMileageDetailContainerVisible && !useSolvdStyles && (
						<div className="odometer-and-mileage-detail-container">
							{isMileageInputVisible ? (
								<p className="four-additional-pictures-mileage-paragraph-first">
									<FormattedMessage id="dashboardMileage.paragraph.first"></FormattedMessage>
								</p>
							) : null}

							{isMileageInputVisible ? this.getOdometerInput() : null}
							{isDashboardMileageTextInputValidationVisible && (
								<div className="mileage-text-input-result">
									<ImageRecognitionFeedback2
										validationId={`${this.dashboardImage?.componentId}_mileage`}
										uploadResult={{
											validationResults: mileageTextValidationResults,
										}}
										feedbackMode={FeedbackMode.Main}
										groupFeedback={groupFeedback}
									/>
								</div>
							)}
						</div>
					)}
				</DetailsContainer>
			</>
		);
	}
}

export default injectIntl(OdometerAndMileageControl);
